import { Schema, Struct } from "effect";
import { StationId } from "../station-id.ts";
import { ChangeId, TaskId, RunId, AttemptId, CommitId, TreeId, PrincipalId, WorktreeId } from "../development-identifiers.ts";

const rest = /* @__PURE__ */ (() => [Schema.Record(Schema.String, Schema.Json)] as const)();
const JsonObject = /* @__PURE__ */ (() => Schema.Record(Schema.String, Schema.Json))();
// The actor owns lifecycle enums and nested policy/checkpoint metadata. The HTTP
// contract checks record structure and identities without mutating durable state.
export const ChangeRecord = /* @__PURE__ */ (() => Schema.StructWithRest(Schema.Struct({
  changeId: ChangeId,
  stationId: StationId,
  title: Schema.String,
  goal: Schema.String,
  targetRef: Schema.String,
  baseCommitId: Schema.optionalKey(CommitId),
  baseTreeId: Schema.optionalKey(TreeId),
  observedTargetCommitId: Schema.optionalKey(CommitId),
  observedTargetRevision: Schema.optionalKey(Schema.Number),
  policyDigest: Schema.String,
  creatingPrincipalId: PrincipalId,
  creatingGrantRevision: Schema.Number,
  lifecycleState: Schema.String,
  initializationKey: Schema.String,
  targetStaleAt: Schema.optionalKey(Schema.Number),
  createdAt: Schema.Number,
  updatedAt: Schema.Number,
  activatedAt: Schema.optionalKey(Schema.Number),
  publishedAt: Schema.optionalKey(Schema.Number),
  cancelledAt: Schema.optionalKey(Schema.Number),
  archivedAt: Schema.optionalKey(Schema.Number)
}), rest))();
export type ChangeRecord = typeof ChangeRecord.Type;
export const TaskRecord = /* @__PURE__ */ (() => Schema.StructWithRest(Schema.Struct({
  taskId: TaskId,
  changeId: ChangeId,
  stableKey: Schema.String,
  title: Schema.String,
  instructions: Schema.String,
  state: Schema.String,
  assignedPrincipalId: Schema.optionalKey(PrincipalId),
  inputCommitId: CommitId,
  inputTreeId: TreeId,
  headCommitId: Schema.optionalKey(CommitId),
  headTreeId: Schema.optionalKey(TreeId),
  createdAt: Schema.Number,
  updatedAt: Schema.Number,
  completedAt: Schema.optionalKey(Schema.Number)
}), rest))();
export type TaskRecord = typeof TaskRecord.Type;
export const RunRecord = /* @__PURE__ */ (() => Schema.StructWithRest(Schema.Struct({
  runId: RunId,
  changeId: ChangeId,
  taskId: Schema.optionalKey(TaskId),
  parentRunId: Schema.optionalKey(RunId),
  runKind: Schema.String,
  inputCommitId: Schema.optionalKey(CommitId),
  inputTreeId: TreeId,
  worktreeId: Schema.optionalKey(WorktreeId),
  actorPrincipalId: PrincipalId,
  runnerKind: Schema.String,
  runnerConfigDigest: Schema.String,
  runnerConfig: JsonObject,
  policyDigest: Schema.String,
  retryPolicyDigest: Schema.String,
  retryPolicy: JsonObject,
  depth: Schema.Number,
  childCount: Schema.Number,
  currentAttemptNumber: Schema.Number,
  executionState: Schema.String,
  observationState: Schema.String,
  outputCommitId: Schema.optionalKey(CommitId),
  outputTreeId: Schema.optionalKey(TreeId),
  failureClass: Schema.optionalKey(Schema.String),
  failure: Schema.optionalKey(Schema.Json),
  deadlineAt: Schema.optionalKey(Schema.Number),
  idempotencyKey: Schema.String,
  createdAt: Schema.Number,
  updatedAt: Schema.Number,
  terminalAt: Schema.optionalKey(Schema.Number)
}), rest))();
export type RunRecord = typeof RunRecord.Type;
export const RunAttemptRecord = /* @__PURE__ */ (() => Schema.StructWithRest(Schema.Struct({
  attemptId: AttemptId,
  runId: RunId,
  attemptNumber: Schema.Number,
  epoch: Schema.Number,
  state: Schema.String,
  runnerOperationId: Schema.String,
  worktreeFence: Schema.optionalKey(Schema.Number),
  checkpointId: Schema.optionalKey(Schema.String),
  checkpoint: Schema.optionalKey(JsonObject),
  pendingSourceOutput: Schema.optionalKey(JsonObject),
  heartbeatAt: Schema.optionalKey(Schema.Number),
  leaseExpiresAt: Schema.optionalKey(Schema.Number),
  startedAt: Schema.Number,
  updatedAt: Schema.Number,
  terminalAt: Schema.optionalKey(Schema.Number),
  outcome: Schema.optionalKey(Schema.Json),
  failure: Schema.optionalKey(Schema.Json),
  retryInput: Schema.optionalKey(JsonObject)
}), rest))();
export type RunAttemptRecord = typeof RunAttemptRecord.Type;
export const RunRetryDecisionRecord = /* @__PURE__ */ (() => Schema.StructWithRest(Schema.Struct({
  attemptId: AttemptId,
  runId: RunId,
  attemptNumber: Schema.Number,
  failureClass: Schema.String,
  action: Schema.String,
  decision: JsonObject,
  immutableInputDigest: Schema.optionalKey(Schema.String),
  retryPolicyDigest: Schema.String,
  retryAt: Schema.optionalKey(Schema.Number),
  nextAttemptNumber: Schema.optionalKey(Schema.Number),
  consumedByAttemptId: Schema.optionalKey(AttemptId),
  poisonQuarantinedUntil: Schema.optionalKey(Schema.Number),
  createdAt: Schema.Number,
  updatedAt: Schema.Number
}), rest))();
export type RunRetryDecisionRecord = typeof RunRetryDecisionRecord.Type;
export const RunEventRecord = /* @__PURE__ */ (() => Schema.StructWithRest(Schema.Struct({
  sequence: Schema.Number,
  eventId: Schema.String,
  changeId: ChangeId,
  taskId: Schema.optionalKey(TaskId),
  runId: Schema.optionalKey(RunId),
  parentRunId: Schema.optionalKey(RunId),
  attemptId: Schema.optionalKey(AttemptId),
  eventType: Schema.String,
  durable: Schema.Boolean,
  payload: Schema.Json,
  createdAt: Schema.Number
}), rest))();
export type RunEventRecord = typeof RunEventRecord.Type;
export const CancellationRecord = /* @__PURE__ */ (() => Schema.StructWithRest(Schema.Struct({
  cancellationId: Schema.String, scope: Schema.String, rootId: Schema.String, reason: Schema.String, source: Schema.String,
  requestDigest: Schema.String, acceptedAt: Schema.Number, cleanupState: Schema.String, cleanupCompletedAt: Schema.optionalKey(Schema.Number),
  mutated: Schema.Boolean, replay: Schema.Boolean, targets: Schema.Array(JsonObject)
}), rest))();
export type CancellationRecord = typeof CancellationRecord.Type;
export const WorktreeAssignment = /* @__PURE__ */ (() => Schema.Struct({
  worktreeId: WorktreeId, createdByPrincipalId: PrincipalId,
  lease: Schema.optionalKey(Schema.NullOr(Schema.Struct({ holderPrincipalId: Schema.optionalKey(PrincipalId) })))
}))();
export type WorktreeAssignment = typeof WorktreeAssignment.Type;
export const ChangePage = /* @__PURE__ */ (() => Schema.StructWithRest(Schema.Struct({ changes: Schema.Array(ChangeRecord), nextCursor: Schema.optionalKey(Schema.String), hasMore: Schema.Boolean }), rest))();
export const TaskPage = /* @__PURE__ */ (() => Schema.StructWithRest(Schema.Struct({ tasks: Schema.Array(TaskRecord), nextCursor: Schema.optionalKey(Schema.String), hasMore: Schema.Boolean }), rest))();
export const RunPage = /* @__PURE__ */ (() => Schema.Struct({ runs: Schema.Array(RunRecord), nextCursor: Schema.optionalKey(Schema.String), hasMore: Schema.Boolean }))();
export const RunEventPage = /* @__PURE__ */ (() => Schema.Struct({ events: Schema.Array(RunEventRecord), requestedAfterSequence: Schema.Number, nextSequence: Schema.Number, hasMore: Schema.Boolean }))();
export type ChangePage = typeof ChangePage.Type;
export type TaskPage = typeof TaskPage.Type;
export type RunPage = typeof RunPage.Type;
export type RunEventPage = typeof RunEventPage.Type;
export const RunCreated = /* @__PURE__ */ (() => Schema.Struct({ run: RunRecord, reservation: Schema.Json }))();
export const CancellationResult = /* @__PURE__ */ (() => Schema.Struct({ cancellation: CancellationRecord }))();
export const DevelopmentResponse = /* @__PURE__ */ (() => Schema.StructWithRest(Schema.Struct({ ok: Schema.Literal(true), stationId: StationId }), rest))();

export const BoundedRun = /* @__PURE__ */ (() => Schema.StructWithRest(Schema.Struct({
  ...Struct.omit(RunRecord.schema.fields, ["runnerConfig"]), runnerConfigKeys: Schema.Array(Schema.String)
}), rest))();
const responseFields = /* @__PURE__ */ (() => ({ ok: Schema.Literal(true), stationId: StationId }))();
const changeResponse = /* @__PURE__ */ (() => Schema.Struct({ ...responseFields, change: ChangeRecord }))();
const taskResponse = /* @__PURE__ */ (() => Schema.Struct({ ...responseFields, task: TaskRecord }))();
const runResponse = /* @__PURE__ */ (() => Schema.Struct({ ...responseFields, run: BoundedRun }))();
const cancellationResponse = /* @__PURE__ */ (() => Schema.Struct({ ...responseFields, cancellation: CancellationRecord }))();
export const DevelopmentResponses = /* @__PURE__ */ (() => ({
  "change.create": changeResponse, "change.inspect": changeResponse,
  "change.list": Schema.Struct({ ...responseFields, page: ChangePage }),
  "change.cancel": cancellationResponse,
  "task.create": taskResponse, "task.inspect": taskResponse,
  "task.list": Schema.Struct({ ...responseFields, page: TaskPage }),
  "run.create": Schema.Struct({ ...responseFields, run: BoundedRun, reservation: Schema.Json }),
  "run.inspect": runResponse,
  "run.list": Schema.Struct({ ...responseFields, page: Schema.Struct({ ...RunPage.fields, runs: Schema.Array(BoundedRun) }) }),
  "run.tail": Schema.Struct({ ...responseFields, ...RunEventPage.fields, changeId: ChangeId, bounded: Schema.Literal(true) }),
  "run.attempt": Schema.Struct({ ...responseFields, attempt: RunAttemptRecord, retryDecision: Schema.optionalKey(RunRetryDecisionRecord) }),
  "run.retry": Schema.Struct({ ...responseFields, runId: RunId, decision: RunRetryDecisionRecord }),
  "run.cancel": cancellationResponse, "run.cleanup": cancellationResponse, "cancellation.inspect": cancellationResponse
}))();
